"use client";

import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import ShellFrame, { RAIL_WIDTH } from "./ShellFrame";
import SolutionAside from "./SolutionAside";
import { usePersistedWidth } from "./shell-primitives";
import {
  SlotRegistryContext,
  SolutionModeContext,
  emptySlotRegistry,
  solutionOnlyMode,
} from "./ArticleShellContext";
import { getRailOnlyShellLabel } from "@/lib/route-layout";

const noop = () => {};

/** 데스크톱 관리자 미리보기: 일반 학습 화면과 같은 셸·풀이 UI를 비저장 모드로 사용한다. */
export default function AdminProblemShell({
  children,
  footer,
  globals,
}: {
  children: ReactNode;
  footer: ReactNode;
  globals: ReactNode;
}) {
  const pathname = usePathname() ?? "";
  const searchParams = useSearchParams();
  const routeKey = `${pathname}?${searchParams.toString()}`;
  return (
    <AdminProblemShellContent key={routeKey} pathname={pathname} footer={footer} globals={globals}>
      {children}
    </AdminProblemShellContent>
  );
}

function AdminProblemShellContent({
  children,
  footer,
  globals,
  pathname,
}: {
  children: ReactNode;
  footer: ReactNode;
  globals: ReactNode;
  pathname: string;
}) {
  const [solutionW, onResizeRight] = usePersistedWidth("ui2-admin-solution-w", 560, 300, 680);
  const [activeSolutionId, setActiveSolutionId] = useState<string | null>(null);
  const [solutionCollapsed, setSolutionCollapsed] = useState(false);
  const [solutionSlot, setSolutionSlot] = useState<HTMLElement | null>(null);
  const solutionSplitRef = useRef<HTMLDivElement>(null);
  const enterSolution = useCallback((id: string) => {
    setActiveSolutionId(id);
    setSolutionCollapsed(false);
  }, []);
  const exitSolution = useCallback(() => {
    setActiveSolutionId(null);
    setSolutionCollapsed(false);
  }, []);
  const slotRegistry = useMemo(() => emptySlotRegistry(true), []);
  const solutionMode = useMemo(
    () => solutionOnlyMode({
      rightMode: activeSolutionId ? "solution" : "panels",
      solutionSlot,
      errorNoteSlot: null,
      activeSolutionId,
      wrongOpen: false,
      solutionCollapsed,
      enterSolution,
      exitSolution,
      setWrongOpen: noop,
    }),
    [activeSolutionId, solutionSlot, solutionCollapsed, enterSolution, exitSolution],
  );
  const problemId = activeSolutionId?.replace(/^problem-/, "");

  return (
    <SlotRegistryContext.Provider value={slotRegistry}>
      <SolutionModeContext.Provider value={solutionMode}>
        <ShellFrame
          columns={activeSolutionId ? "left+main+right" : "left+main"}
          leftWidthCss={`${RAIL_WIDTH}px`}
          showLeftContent={false}
          rightWidthCss={`${solutionCollapsed ? 52 : solutionW}px`}
          onResizeRight={onResizeRight}
          showRightResize={!solutionCollapsed}
          rightAsideClassName="bg-gray-50 dark:bg-gray-900/40"
          rightAriaLabel={`문제 ${problemId ?? ""} 풀이`}
          header={{ categoryLabel: "", staticTitle: getRailOnlyShellLabel(pathname), showSidebarToggle: false }}
          right={activeSolutionId && (
            <SolutionAside
              activeSolutionId={activeSolutionId}
              problemLabel={problemId}
              showErrorNote={false}
              solutionCollapsed={solutionCollapsed}
              onHide={() => setSolutionCollapsed(true)}
              onExpand={() => setSolutionCollapsed(false)}
              onExit={exitSolution}
              wrongOpen={false}
              setWrongOpen={noop}
              splitRatio={0.5}
              solutionSplitRef={solutionSplitRef}
              onResizeSolutionSplit={noop}
              setSolutionSlot={setSolutionSlot}
              setErrorNoteSlot={noop}
            />
          )}
        >
          {children}
          {footer}
        </ShellFrame>
        {globals}
      </SolutionModeContext.Provider>
    </SlotRegistryContext.Provider>
  );
}
